import { useState, useEffect } from "react";
import { useAccount, useReadContract } from "wagmi";
import { erc20Abi, parseEther } from "viem";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showError, showSuccess, showLoading, dismissToast } from "@/lib/toast";
import { formatEtherWithDecimals, formatNumberInput } from "@/lib/utils";
import { USDT_ADDRESS } from "@/config/env";
import { usePlatFormConfig } from "@/hooks/api/usePlatFormConfig";
import { useSubmitWithdrawOrDepositTx } from "@/hooks/api/useSubmitWithdrawOrDepositTx";
import { useQueryClient } from "@tanstack/react-query";
import { parseChainError } from "@/lib/chainError";
import { sendTokenDepositUsePayContract } from "@/lib/evm";
import { C } from "./profileTheme";
import { useTranslation } from "react-i18next";
import { getTokenName } from "@/config/common";
import { ApiGetDepositRecipient } from "@/api";

export function DepositDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { address } = useAccount();
  const queryClient = useQueryClient();
  const { data: platFormConfig } = usePlatFormConfig();
  const { mutateAsync: submitDepositTx } = useSubmitWithdrawOrDepositTx();

  const [amount, setAmount] = useState("");
  const [depositing, setDepositing] = useState(false);

  const { data: walletUsdtBalance, refetch: refetchBalance } = useReadContract({
    address: USDT_ADDRESS as `0x${string}`,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
  });

  const balanceWei = (walletUsdtBalance as bigint) ?? BigInt(0);
  const balanceFormatted = formatEtherWithDecimals(balanceWei);

  useEffect(() => {
    if (open) {
      refetchBalance();
    } else {
      setAmount("");
    }
  }, [open, refetchBalance]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const max = Number(balanceFormatted);
    const [formatted, isGtMax] = formatNumberInput(value, max);
    setAmount(formatted);
    if (isGtMax) showError(t("profilePage.depositDialog.insufficientBalance"));
  };

  const setQuickAmount = (pct: number) => {
    const max = Number(balanceFormatted);
    const val = (max * pct).toFixed(2);
    setAmount(val);
  };

  const handleConfirm = async () => {
    if (!address) {
      showError(t("profilePage.depositDialog.connectWalletFirst"));
      return;
    }
    const res = await ApiGetDepositRecipient({});
    console.log("ApiGetDepositRecipient", res);
    if (!res || !res?.data.depositRecipient) {
      showError(t("profilePage.depositDialog.configError"));
      return;
    }
    const numAmount = parseFloat(amount) || 0;
    if (!amount || numAmount <= 0) {
      showError(t("profilePage.depositDialog.enterAmount"));
      return;
    }
    if (numAmount > Number(balanceFormatted)) {
      showError(t("profilePage.depositDialog.insufficientBalance"));
      return;
    }

    const amountWei = parseEther(amount).toString();
    const orderId = `deposit_${Date.now()}`;

    try {
      setDepositing(true);
      showLoading(t("profilePage.depositDialog.sendingTx"), "deposit-tx");

      const [txHash, payErr] = await sendTokenDepositUsePayContract({
        sender: address,
        tokenAddress: USDT_ADDRESS as `0x${string}`,
        recipient: res?.data.depositRecipient as `0x${string}`,
        amountWei,
        orderId,
      });

      if (payErr || !txHash) {
        dismissToast("deposit-tx");
        throw payErr ?? new Error(t("profilePage.depositDialog.txFailed"));
      }
      dismissToast("deposit-tx");

      showLoading(
        t("profilePage.depositDialog.submittingTx"),
        "deposit-submit"
      );
      const submitRes = await submitDepositTx({
        txId: txHash,
        amount: amountWei,
        opCode: 1,
        coinContract: USDT_ADDRESS,
      });
      dismissToast("deposit-submit");

      if (submitRes?.resultCode !== 0) {
        showError(t("profilePage.depositDialog.submitFailed"));
        return;
      }

      showSuccess(t("profilePage.depositDialog.success"));
      queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
      queryClient.invalidateQueries({
        queryKey: ["withdrawAndRechargeRecord"],
      });
      onClose();
    } catch (err) {
      dismissToast("deposit-tx");
      dismissToast("deposit-submit");
      const msg = parseChainError(err);
      showError(t(msg));
    } finally {
      setDepositing(false);
    }
  };

  const handleClose = () => {
    if (depositing) return;
    setAmount("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
        onOpenAutoFocus={event => {
          // 阻止 Dialog 打开时自动聚焦首个可聚焦元素（避免唤起移动端输入法）
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("profilePage.depositDialog.title", {
              tokenNameUsdt: getTokenName(USDT_ADDRESS),
            })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="rounded-lg p-3 text-xs space-y-2"
            style={{ background: C.card, border: `1px solid ${C.border}` }}
          >
            <div className="flex justify-between items-center">
              <span style={{ color: C.dim }}>
                {t("profilePage.depositDialog.network")}
              </span>
              <span>BNB Chain (BEP-20)</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: C.dim }}>
                {t("profilePage.depositDialog.fee")}
              </span>
              <span style={{ color: C.green }}>
                {t("profilePage.depositDialog.feeValue")}
              </span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs" style={{ color: C.dim }}>
                {t("profilePage.depositDialog.amount")}
              </p>
              <p className="text-xs" style={{ color: C.muted }}>
                {t("profilePage.depositDialog.availableBalance", {
                  value: balanceFormatted,
                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                })}
              </p>
            </div>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                placeholder={t("profilePage.depositDialog.placeholder")}
                disabled={depositing}
                className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  color: C.text,
                }}
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: C.dim }}
              >
                USDT
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "25%", pct: 0.25 },
              { label: "50%", pct: 0.5 },
              { label: "75%", pct: 0.75 },
              { label: t("profilePage.depositDialog.all"), pct: 1 },
            ].map(({ label, pct }) => (
              <button
                key={label}
                disabled={depositing}
                onClick={() =>
                  pct < 1 ? setQuickAmount(pct) : setAmount(balanceFormatted)
                }
                className="py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            className="w-full py-3 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={depositing || !amount || (parseFloat(amount) || 0) <= 0}
            onClick={handleConfirm}
          >
            {depositing
              ? t("profilePage.depositDialog.confirming")
              : t("profilePage.depositDialog.confirm")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
